import { getShowroomConfig, showroomRequest, showroomToast, toNumericId } from "./showroom-api";

// Each product is one color of a style, so the showroom tracks it by product id;
// on the PDP the form's selected variant `id` wins over data-showroom-variant-id.

const buttonSelector = "[data-add-to-showroom]";

let showroomItemsPromise = null;

// The customer's showroom items, fetched once per page and shared by every
// button (and by cart attribution below).
export function getShowroomItems() {
  if (showroomItemsPromise) return showroomItemsPromise;

  const { customer, maintenance } = getShowroomConfig();

  if (!customer?.showroom || maintenance) {
    showroomItemsPromise = Promise.resolve([]);
    return showroomItemsPromise;
  }

  showroomItemsPromise = showroomRequest("getShowroomProductVariants", {
    method: "GET",
    body: { email: customer.email },
  }).then(({ ok, data }) => {
    const items = ok && Array.isArray(data?.list) ? data.list : [];
    window.showroomVariantIds = new Set(items.map((item) => String(item.variant)));
    return items;
  });

  return showroomItemsPromise;
}

function setButtonActive(button, active) {
  const { strings = {} } = getShowroomConfig();

  button.setAttribute("aria-pressed", String(active));
  button.classList.toggle("is-active", active);

  const defaultIcon = button.querySelector("[data-showroom-icon-default]");
  const filledIcon = button.querySelector("[data-showroom-icon-filled]");
  if (defaultIcon) defaultIcon.hidden = active;
  if (filledIcon) filledIcon.hidden = !active;

  const label = button.querySelector("[data-showroom-label]");
  if (label) label.textContent = active ? strings.removeFromShowroom : strings.addToShowroom;
}

function markButton(button, items) {
  const productId = toNumericId(button.dataset.showroomProductId);
  const active = items.some((item) => String(item.product) === productId);

  setButtonActive(button, active);
}

export function markShowroomButtons(root = document) {
  const buttons = root.querySelectorAll(buttonSelector);
  if (!buttons.length) return;

  getShowroomItems().then((items) => buttons.forEach((button) => markButton(button, items)));
}

// Product cards can swap to another color client-side (swatch clicks, grouped
// PLP hydration); keep the card's showroom button pointed at the shown product.
export function syncShowroomButton(card, productId, variantId) {
  const button = card?.querySelector(buttonSelector);
  if (!button || !productId) return;

  button.dataset.showroomProductId = toNumericId(productId);
  if (variantId) button.dataset.showroomVariantId = toNumericId(variantId);

  getShowroomItems().then((items) => markButton(button, items));
}

function getVariantId(button) {
  const formVariant = button.form?.elements.namedItem("id")?.value;

  return toNumericId(formVariant || button.dataset.showroomVariantId);
}

// Adds (variantId) or removes (the whole product) and flips every button for
// that product — a card and the PDP, or a card that appears twice.
async function updateShowroom(button, { remove, productId, variantId }) {
  const { customer, strings = {} } = getShowroomConfig();

  button.classList.add("is-loading");
  button.disabled = true;

  const request = remove
    ? showroomRequest("removeProductVariantFromShowroom", { body: { email: customer.email, product: productId } })
    : showroomRequest("addProductVariantToShowroom", { body: { email: customer.email, variant: variantId } });

  const { ok, error } = await request;

  button.classList.remove("is-loading");
  button.disabled = false;

  // The app refuses a color that's already in the showroom's styles —
  // which is where the shopper wanted it, so that counts as added.
  const alreadyAdded = !ok && !remove && String(error).includes("STYLE_ALREADY_EXISTS_SHOWROOM");
  if (!ok) {
    if (!alreadyAdded) {
      showroomToast(remove ? strings.removeError : strings.addError);
      return false;
    }
  }

  document
    .querySelectorAll(`${buttonSelector}[data-showroom-product-id="${productId}"]`)
    .forEach((match) => setButtonActive(match, !remove));

  // Refetch so cart attribution sees the change right away.
  showroomItemsPromise = null;
  getShowroomItems();
  showroomToast(remove ? strings.removed : alreadyAdded ? strings.alreadyAdded : strings.added);
  return true;
}

// The active showroom's Style Lineup tab.
function showroomLineupUrl() {
  const { pageUrl = "/pages/showroom", customer } = getShowroomConfig();
  const params = new URLSearchParams({ active_section: "lineup" });
  if (customer?.showroom) params.set("id", customer.showroom);

  return `${pageUrl}?${params}`;
}

function toggleShowroom(button) {
  const remove = button.getAttribute("aria-pressed") === "true";
  const productId = toNumericId(button.dataset.showroomProductId);

  // Removing is direct; without the wishlist drawer there's no picker either.
  if (remove || !document.querySelector("wishlist-drawer")) {
    updateShowroom(button, { remove, productId, variantId: getVariantId(button) });
    return;
  }

  openColorPicker(button, productId);
}

// What the picker opens on: a card's own image and badges, or on the PDP the
// largest gallery image (as the PDP wishlist button does).
function getPickerSource(button) {
  const card = button.closest("[data-product-card]");

  if (card) {
    // The color's own image, not the Product Group main image standing in for it.
    const ownImages = card.querySelector("template[data-product-card-own-images]")?.content;
    const image = (ownImages || card).querySelector("[data-product-card-image] img");

    return {
      productGroupId: card.dataset.productGroupId,
      color: card.dataset.color,
      image,
      badges: [...card.querySelectorAll("[data-product-card-badges] .badge")].map((el) => el.textContent.trim()),
    };
  }

  const image = [...document.querySelectorAll("[data-product-media] img, .media-gallery img")].sort(
    (a, b) => b.naturalWidth - a.naturalWidth,
  )[0];

  return { productGroupId: button.dataset.productGroupId, color: button.dataset.color, image, badges: [] };
}

// The wishlist drawer's "Select Option" picker, relabelled "Add to Showroom".
function openColorPicker(button, productId) {
  const { strings = {} } = getShowroomConfig();
  const source = getPickerSource(button);

  const detail = {
    productId: `gid://shopify/Product/${productId}`,
    productGroupId: source.productGroupId || "",
    color: source.color || "",
    image: source.image?.currentSrc || source.image?.src || "",
    imageAlt: source.image?.alt || "",
    badges: source.badges,
    swymProductId: productId,
    // On the PDP this is the selected size, kept when the color isn't changed.
    swymVariantId: getVariantId(button),
    addLabel: strings.addToShowroomButton || strings.addToShowroom,
    // Stays open after adding; a color already in the showroom's styles shows
    // "Go to Showroom" instead, which opens its Style Lineup.
    keepOpen: true,
    doneLabel: strings.goToShowroom,
    isDone: async (selection) => {
      const selectedProductId = toNumericId(selection.productId) || productId;
      const items = await getShowroomItems();
      return items.some((item) => String(item.product) === String(selectedProductId));
    },
    onDone: () => {
      window.location.href = showroomLineupUrl();
    },
    onSelect: (selection) => {
      const selectedProductId = toNumericId(selection.productId) || productId;
      const selectedVariantId = toNumericId(selection.variantId) || getVariantId(button);

      return updateShowroom(button, { remove: false, productId: selectedProductId, variantId: selectedVariantId });
    },
  };

  document.dispatchEvent(new CustomEvent("drawer:before-open", { detail: { name: "wishlist" } }));
  document.dispatchEvent(new CustomEvent("wishlist:open", { detail }));
}

// The showroom drawer shows the maintenance message (maintenance mode) or a
// sign-in message (login mode, guests only) — see snippet.showroom-config.
function openShowroomDrawer() {
  document.dispatchEvent(new CustomEvent("drawer:before-open", { detail: { name: "showroom" } }));
  document.dispatchEvent(new CustomEvent("showroom:open"));
}

function handleClick(event) {
  const button = event.target.closest(buttonSelector);
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();

  const { maintenance, customer } = getShowroomConfig();

  if (maintenance || !customer) {
    openShowroomDrawer();
    return;
  }

  // No showroom yet: start one instead of failing the add.
  if (!customer?.showroom) {
    document.dispatchEvent(new CustomEvent("showroom-setup:open"));
    return;
  }

  toggleShowroom(button);
}

// Tags cart lines whose variant is in the customer's showroom so the showroom
// app can mark that member as "Purchased" when the order comes in. Called
// synchronously by window.addToCart, so it only uses the already-fetched list.
window.applyShowroomAttribution = (item) => {
  const { customer } = getShowroomConfig();
  const variantIds = window.showroomVariantIds;

  if (!customer?.showroom || !variantIds || !item?.id || !variantIds.has(String(item.id))) {
    return item;
  }

  return {
    ...item,
    properties: {
      ...(item.properties || {}),
      _showroom_data: `${customer.showroom}:${customer.email}`,
    },
  };
};

// "Create a showroom" triggers anywhere on the site ([data-showroom-create]).
// The wizard itself (components/showroom-setup) only renders for signed-in
// customers, so guests get the drawer's sign-in message first.
function handleCreateClick(event) {
  const trigger = event.target.closest("[data-showroom-create]");
  if (!trigger) return;

  event.preventDefault();

  const { maintenance, customer } = getShowroomConfig();

  if (maintenance || !customer) {
    openShowroomDrawer();
    return;
  }

  document.dispatchEvent(new CustomEvent("showroom-setup:open"));
}

// Section bundles outside global.min.js (e.g. main-collection's grouped cards)
// call these through window rather than importing this module, which would
// register a second click listener.
window.syncShowroomButton = syncShowroomButton;
window.markShowroomButtons = markShowroomButtons;

const init = () => {
  if (!getShowroomConfig().enabled) return;

  markShowroomButtons();
  getShowroomItems();
};

document.addEventListener("click", handleClick);
document.addEventListener("click", handleCreateClick);
document.addEventListener("product:ajax-rendered", () => markShowroomButtons());

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
